import type { AppErrorDomain, AppErrorFamily, AppErrorSeverity } from "./types";

export interface CatalogEntry {
  domain: AppErrorDomain;
  error_family: AppErrorFamily;
  user_title: string;
  user_message: string;
  user_action?: string;
  severity: AppErrorSeverity;
  retry_allowed: boolean;
}

export const ERROR_CATALOG: Record<string, CatalogEntry> = {
  "AUTH-001": {
    domain: "auth",
    error_family: "authentication_required",
    user_title: "Session expired",
    user_message: "Your session has expired. Please sign in again to continue.",
    user_action: "Sign in again",
    severity: "warning",
    retry_allowed: false,
  },
  "AUTH-002": {
    domain: "auth",
    error_family: "validation",
    user_title: "Incorrect password",
    user_message: "The password you entered is incorrect. Please try again.",
    user_action: "Check your password and try again.",
    severity: "error",
    retry_allowed: true,
  },
  "AUTH-003": {
    domain: "auth",
    error_family: "not_found",
    user_title: "Account not found",
    user_message: "No account was found with this email address.",
    user_action: "Check your email address or create a new account.",
    severity: "error",
    retry_allowed: true,
  },
  "AUTH-004": {
    domain: "auth",
    error_family: "permission_denied",
    user_title: "Access denied",
    user_message: "You do not have permission to access this resource.",
    severity: "error",
    retry_allowed: false,
  },
  "NET-001": {
    domain: "system",
    error_family: "network",
    user_title: "Connection problem",
    user_message: "The request could not reach the server. Please check your connection and try again.",
    severity: "warning",
    retry_allowed: true,
  },
  "NET-002": {
    domain: "system",
    error_family: "timeout",
    user_title: "Request timed out",
    user_message: "The request took too long to complete. Please try again.",
    severity: "warning",
    retry_allowed: true,
  },
  "SUB-001": {
    domain: "subscription",
    error_family: "not_found",
    user_title: "Subscription not found",
    user_message: "The subscription could not be found.",
    severity: "error",
    retry_allowed: false,
  },
  "SUB-002": {
    domain: "subscription",
    error_family: "state_conflict",
    user_title: "Subscription already cancelled",
    user_message: "This subscription has already been scheduled for cancellation.",
    severity: "warning",
    retry_allowed: false,
  },
  "SUB-003": {
    domain: "subscription",
    error_family: "permission_denied",
    user_title: "Admin required",
    user_message: "Only organization administrators can manage subscriptions.",
    severity: "error",
    retry_allowed: false,
  },
  "SUB-006": {
    domain: "subscription",
    error_family: "processing_failed",
    user_title: "Subscription update failed",
    user_message: "We could not update the subscription. Please try again.",
    severity: "error",
    retry_allowed: true,
  },
  "SUB-007": {
    domain: "subscription",
    error_family: "validation",
    user_title: "Invalid subscription request",
    user_message: "The requested subscription change is not valid.",
    severity: "error",
    retry_allowed: true,
  },
  "SUB-008": {
    domain: "subscription",
    error_family: "state_conflict",
    user_title: "Nothing to revoke",
    user_message: "There is no pending cancellation to revoke.",
    severity: "warning",
    retry_allowed: false,
  },
  "TEAM-001": {
    domain: "team",
    error_family: "permission_denied",
    user_title: "Team access denied",
    user_message: "You do not have permission to manage this team resource.",
    severity: "error",
    retry_allowed: false,
  },
  "TEAM-002": {
    domain: "team",
    error_family: "already_exists",
    user_title: "Already exists",
    user_message: "This record already exists.",
    severity: "warning",
    retry_allowed: false,
  },
  "CV-001": {
    domain: "cv",
    error_family: "validation",
    user_title: "Unsupported file type",
    user_message: "Please upload a PDF, JPG, PNG, or WEBP file.",
    severity: "error",
    retry_allowed: true,
  },
  "CV-002": {
    domain: "cv",
    error_family: "validation",
    user_title: "File too large",
    user_message: "The file is too large to process.",
    severity: "error",
    retry_allowed: true,
  },
  "CV-003": {
    domain: "cv",
    error_family: "storage_failure",
    user_title: "Upload failed",
    user_message: "The file could not be uploaded. Please try again.",
    severity: "error",
    retry_allowed: true,
  },
  "CV-004": {
    domain: "cv",
    error_family: "processing_failed",
    user_title: "CV unreadable",
    user_message: "The system could not read this CV.",
    severity: "error",
    retry_allowed: true,
  },
  "CV-005": {
    domain: "cv",
    error_family: "processing_failed",
    user_title: "CV parsing failed",
    user_message: "The system could not extract data from this CV.",
    severity: "error",
    retry_allowed: true,
  },
  "CV-006": {
    domain: "cv",
    error_family: "processing_failed",
    user_title: "CV scoring failed",
    user_message: "The CV could not be scored right now.",
    severity: "error",
    retry_allowed: true,
  },
  "CV-008": {
    domain: "cv",
    error_family: "state_conflict",
    user_title: "CV processing already running",
    user_message: "This CV is already being processed. Please wait for the current run to finish.",
    severity: "warning",
    retry_allowed: false,
  },
  "AIINT-001": {
    domain: "ai_interview",
    error_family: "dependency_failure",
    user_title: "AI service unavailable",
    user_message: "The AI service is temporarily unavailable. Please try again later.",
    severity: "warning",
    retry_allowed: true,
  },
  "AIINT-002": {
    domain: "ai_interview",
    error_family: "timeout",
    user_title: "AI request timed out",
    user_message: "The AI request took too long to complete. Please try again.",
    severity: "warning",
    retry_allowed: true,
  },
  "INT-001": {
    domain: "interview",
    error_family: "not_found",
    user_title: "Interview not found",
    user_message: "The interview session could not be found.",
    severity: "error",
    retry_allowed: false,
  },
  "INT-002": {
    domain: "interview",
    error_family: "expired",
    user_title: "Interview expired",
    user_message: "This interview invitation is no longer valid.",
    severity: "error",
    retry_allowed: false,
  },
  "INT-003": {
    domain: "interview",
    error_family: "cancelled",
    user_title: "Interview cancelled",
    user_message: "This interview invitation has been cancelled.",
    severity: "warning",
    retry_allowed: false,
  },
  "INT-004": {
    domain: "interview",
    error_family: "state_conflict",
    user_title: "Interview already completed",
    user_message: "This interview session has already been completed.",
    severity: "info",
    retry_allowed: false,
  },
  "INT-005": {
    domain: "interview",
    error_family: "validation",
    user_title: "Invalid interview link",
    user_message: "The interview link is invalid.",
    severity: "error",
    retry_allowed: false,
  },
  "INT-010": {
    domain: "interview",
    error_family: "state_conflict",
    user_title: "Already processing",
    user_message: "This interview action is already in progress.",
    severity: "warning",
    retry_allowed: false,
  },
  "APP-001": {
    domain: "application",
    error_family: "not_found",
    user_title: "Application not found",
    user_message: "The application could not be found.",
    severity: "error",
    retry_allowed: false,
  },
  "APP-002": {
    domain: "application",
    error_family: "duplicate_action",
    user_title: "Already applied",
    user_message: "You have already applied for this job.",
    severity: "warning",
    retry_allowed: false,
  },
  "APP-003": {
    domain: "application",
    error_family: "state_conflict",
    user_title: "Application already withdrawn",
    user_message: "This application has already been withdrawn.",
    severity: "warning",
    retry_allowed: false,
  },
  "JOB-001": {
    domain: "job",
    error_family: "not_found",
    user_title: "Job not found",
    user_message: "The job could not be found.",
    severity: "error",
    retry_allowed: false,
  },
  "JOB-002": {
    domain: "job",
    error_family: "state_conflict",
    user_title: "Job closed",
    user_message: "This job is no longer accepting applications.",
    severity: "warning",
    retry_allowed: false,
  },
  "JOB-003": {
    domain: "job",
    error_family: "validation",
    user_title: "Missing job details",
    user_message: "Please fill in all required job fields before continuing.",
    severity: "error",
    retry_allowed: true,
  },
  "JOB-006": {
    domain: "job",
    error_family: "processing_failed",
    user_title: "Stage change failed",
    user_message: "The candidate stage could not be updated.",
    severity: "error",
    retry_allowed: true,
  },
  "PSVC-001": {
    domain: "admin",
    error_family: "already_exists",
    user_title: "Service already mapped",
    user_message: "This service is already mapped to the selected plan.",
    severity: "warning",
    retry_allowed: false,
  },
  "PSVC-002": {
    domain: "admin",
    error_family: "validation",
    user_title: "Platform mismatch",
    user_message: "The selected service does not belong to the same platform as the plan.",
    severity: "error",
    retry_allowed: false,
  },
  "PSVC-003": {
    domain: "admin",
    error_family: "state_conflict",
    user_title: "Publish blocked",
    user_message: "This plan-service mapping cannot be published until its blockers are resolved.",
    severity: "warning",
    retry_allowed: false,
  },
  "OFR-001": {
    domain: "offer",
    error_family: "not_found",
    user_title: "Offer not found",
    user_message: "The offer could not be found.",
    severity: "error",
    retry_allowed: false,
  },
  "OFR-002": {
    domain: "offer",
    error_family: "expired",
    user_title: "Offer expired",
    user_message: "This offer has expired.",
    severity: "warning",
    retry_allowed: false,
  },
  "OFR-003": {
    domain: "offer",
    error_family: "state_conflict",
    user_title: "Offer no longer actionable",
    user_message: "This offer can no longer be updated.",
    severity: "warning",
    retry_allowed: false,
  },
  "MERGE-001": {
    domain: "profile",
    error_family: "processing_failed",
    user_title: "Merge failed",
    user_message: "The system could not merge the data correctly.",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-001": {
    domain: "billing",
    error_family: "validation",
    user_title: "VAT number format",
    user_message: "The VAT number must be 15 digits and start and end with the digit 3.",
    user_action: "Check the VAT number and try again",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-002": {
    domain: "billing",
    error_family: "validation",
    user_title: "Additional ID is incomplete",
    user_message: "Both the additional commercial ID type and value must be provided together.",
    user_action: "Fill in both fields, or clear both",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-003": {
    domain: "billing",
    error_family: "validation",
    user_title: "Invalid ID type",
    user_message: "The selected additional ID type is not supported.",
    user_action: "Choose a supported ID type",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-004": {
    domain: "billing",
    error_family: "validation",
    user_title: "Invalid ID value",
    user_message: "The additional ID value contains characters that are not allowed.",
    user_action: "Use only letters, digits, and . _ / - characters",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-005": {
    domain: "billing",
    error_family: "validation",
    user_title: "Additional commercial ID required",
    user_message: "For B2B billing, an additional commercial ID such as CR or 700 is required.",
    user_action: "Add an additional commercial ID",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-006": {
    domain: "billing",
    error_family: "validation",
    user_title: "Legal name required",
    user_message: "The legal entity name must be provided for B2B billing.",
    user_action: "Add the legal entity name",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-007": {
    domain: "billing",
    error_family: "validation",
    user_title: "Country code",
    user_message: "The country must be a valid 2-letter ISO country code.",
    user_action: "Select a valid country",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-008": {
    domain: "billing",
    error_family: "validation",
    user_title: "Postal code",
    user_message: "A valid postal code is required. In Saudi Arabia it must be exactly 5 digits.",
    user_action: "Enter a valid postal code",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-009": {
    domain: "billing",
    error_family: "validation",
    user_title: "Building number",
    user_message: "A valid building number is required. In Saudi Arabia it must be exactly 4 digits.",
    user_action: "Enter a valid building number",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-010": {
    domain: "billing",
    error_family: "validation",
    user_title: "Plot identification",
    user_message: "A valid plot identification is required. In Saudi Arabia it must be exactly 4 digits.",
    user_action: "Enter a valid plot identification",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-011": {
    domain: "billing",
    error_family: "validation",
    user_title: "Street name required",
    user_message: "The street name is required and must be 127 characters or fewer.",
    user_action: "Enter the street name",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-012": {
    domain: "billing",
    error_family: "validation",
    user_title: "City required",
    user_message: "The city name is required and must be 127 characters or fewer.",
    user_action: "Enter the city name",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-013": {
    domain: "billing",
    error_family: "validation",
    user_title: "District required",
    user_message: "The district is required for B2B billing addresses.",
    user_action: "Enter the district",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-014": {
    domain: "billing",
    error_family: "validation",
    user_title: "Region required",
    user_message: "The region (country subdivision) is required for B2B billing.",
    user_action: "Select a region",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-015": {
    domain: "billing",
    error_family: "validation",
    user_title: "Business ID required",
    user_message: "For a buyer party, the additional ID must be a business identifier such as CR or 700, not a personal one.",
    user_action: "Use a business ID type",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-016": {
    domain: "billing",
    error_family: "validation",
    user_title: "Billing identity incomplete",
    user_message: "The B2B billing identity is missing required information. Complete it before issuing invoices or charging.",
    user_action: "Open billing settings to complete the identity",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-017": {
    domain: "billing",
    error_family: "validation",
    user_title: "Billing contact required",
    user_message: "A billing contact must be assigned before invoices can be issued.",
    user_action: "Assign a billing contact",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-IDENT-018": {
    domain: "billing",
    error_family: "permission_denied",
    user_title: "Billing contact role",
    user_message: "The assigned billing contact does not have the required role to manage billing.",
    user_action: "Choose a contact with billing permissions",
    severity: "error",
    retry_allowed: true,
  },
  "BILL-PAY-001": {
    domain: "billing",
    error_family: "configuration_error",
    user_title: "Payment checkout unavailable",
    user_message: "Payments are temporarily unavailable because checkout configuration is incomplete.",
    user_action: "Please contact support. Retrying will not create a charge.",
    severity: "error",
    retry_allowed: false,
  },
  "SYS-001": {
    domain: "system",
    error_family: "unexpected",
    user_title: "Something went wrong",
    user_message: "The system could not complete this request due to an unexpected issue.",
    user_action: "Please try again. If the issue continues, contact support.",
    severity: "error",
    retry_allowed: true,
  },
};

export function getCatalogEntry(errorCode: string): CatalogEntry {
  return ERROR_CATALOG[errorCode] ?? ERROR_CATALOG["SYS-001"];
}
